import React from "react";
import { Text, View } from "react-native";
import { EStyleSheet, useDynamicStyles } from "react-native-extended-stylesheet-typescript";
import { GlobalStyles } from "../style/Style";
import { useServices } from "../../contexts/ServiceContext";
import { PickEmButton } from "./PickEmButton";

export interface PickEmPropProps {
    index: number;
    disabled: boolean;
    isActive: boolean;
    reviewPicksState?: boolean;
    horseInfo: { horseName: string; odds: string; saddleNumber: number };
    onSelect: () => void;
}

export const HorseLine = ({
    index,
    isActive,
    horseInfo,
    onSelect,
    disabled,
    reviewPicksState,
}: PickEmPropProps): JSX.Element => {
    // eslint-disable-next-line
    const { edgeDeploy } = useServices();
    const styles = useDynamicStyles(generateStyles);

    const [backgroundColour, textColour] = setPlaceColours(index + 1);

    const placeTextColourStyle = [{ color: textColour }];
    const backgroundColourStyle = [{ backgroundColor: backgroundColour }];

    let secondBorder;
    if (index === 1) {
        secondBorder = [
            {
                borderColor: textColour,
                borderWidth: EStyleSheet.value(GlobalStyles.BORDER_THICKNESS),
            },
        ];
    }

    return (
        <View style={[styles.prop]}>
            <View style={[styles.placeContainer, backgroundColourStyle, secondBorder]}>
                <Text style={[styles.placeText, placeTextColourStyle]}>{horseInfo.saddleNumber}</Text>
            </View>
            <Text style={styles.horseName}>{horseInfo.horseName}</Text>
            <Text style={styles.odds}>{horseInfo.odds}</Text>
            {!reviewPicksState && (
                <PickEmButton
                    buttonText={isActive ? "Remove" : "Pick"}
                    disabled={disabled}
                    width={70}
                    height={24}
                    margin={{ right: 5, left: 5, bottom: 0 }}
                    isActive={isActive}
                    onPress={onSelect}
                />
            )}
        </View>
    );
};

export const setPlaceColours = (place: number): [string, string] => {
    let background = "";
    let text = "";
    switch (place) {
        case 1: {
            background = EStyleSheet.value(GlobalStyles.HR_HORSE_ONE_BACKGROUND);
            text = EStyleSheet.value(GlobalStyles.HR_HORSE_ONE_TEXT);
            break;
        }
        case 2: {
            background = EStyleSheet.value(GlobalStyles.HR_HORSE_TWO_BACKGROUND);
            text = EStyleSheet.value(GlobalStyles.HR_HORSE_TWO_TEXT);
            break;
        }
        case 3: {
            background = EStyleSheet.value(GlobalStyles.HR_HORSE_THREE_BACKGROUND);
            text = EStyleSheet.value(GlobalStyles.HR_HORSE_THREE_TEXT);
            break;
        }
        case 4: {
            background = EStyleSheet.value(GlobalStyles.HR_HORSE_FOUR_BACKGROUND);
            text = EStyleSheet.value(GlobalStyles.HR_HORSE_FOUR_TEXT);
            break;
        }
        case 5: {
            background = EStyleSheet.value(GlobalStyles.HR_HORSE_FIVE_BACKGROUND);
            text = EStyleSheet.value(GlobalStyles.HR_HORSE_FIVE_TEXT);
            break;
        }
        case 6: {
            background = EStyleSheet.value(GlobalStyles.HR_HORSE_SIX_BACKGROUND);
            text = EStyleSheet.value(GlobalStyles.HR_HORSE_SIX_TEXT);
            break;
        }
        case 7: {
            background = EStyleSheet.value(GlobalStyles.HR_HORSE_SEVEN_BACKGROUND);
            text = EStyleSheet.value(GlobalStyles.HR_HORSE_SEVEN_TEXT);
            break;
        }
        case 8: {
            background = EStyleSheet.value(GlobalStyles.HR_HORSE_EIGHT_BACKGROUND);
            text = EStyleSheet.value(GlobalStyles.HR_HORSE_EIGHT_TEXT);
            break;
        }
        case 9: {
            background = EStyleSheet.value(GlobalStyles.HR_HORSE_NINE_BACKGROUND);
            text = EStyleSheet.value(GlobalStyles.HR_HORSE_NINE_TEXT);
            break;
        }
        case 10: {
            background = EStyleSheet.value(GlobalStyles.HR_HORSE_TEN_BACKGROUND);
            text = EStyleSheet.value(GlobalStyles.HR_HORSE_TEN_TEXT);
            break;
        }
        case 11: {
            background = EStyleSheet.value(GlobalStyles.HR_HORSE_ELEVEN_BACKGROUND);
            text = EStyleSheet.value(GlobalStyles.HR_HORSE_ELEVEN_TEXT);
            break;
        }
        case 12: {
            background = EStyleSheet.value(GlobalStyles.HR_HORSE_TWELVE_BACKGROUND);
            text = EStyleSheet.value(GlobalStyles.HR_HORSE_TWELVE_TEXT);
            break;
        }
        case 13: {
            background = EStyleSheet.value(GlobalStyles.HR_HORSE_THIRTEEN_BACKGROUND);
            text = EStyleSheet.value(GlobalStyles.HR_HORSE_THIRTEEN_TEXT);
            break;
        }
        case 14: {
            background = EStyleSheet.value(GlobalStyles.HR_HORSE_FOURTEEN_BACKGROUND);
            text = EStyleSheet.value(GlobalStyles.HR_HORSE_FOURTEEN_TEXT);
            break;
        }
        case 15: {
            background = EStyleSheet.value(GlobalStyles.HR_HORSE_FIFTEEN_BACKGROUND);
            text = EStyleSheet.value(GlobalStyles.HR_HORSE_FIFTEEN_TEXT);
            break;
        }
        case 16: {
            background = EStyleSheet.value(GlobalStyles.HR_HORSE_SIXTEEN_BACKGROUND);
            text = EStyleSheet.value(GlobalStyles.HR_HORSE_SIXTEEN_TEXT);
            break;
        }
        case 17: {
            background = EStyleSheet.value(GlobalStyles.HR_HORSE_SEVENTEEN_BACKGROUND);
            text = EStyleSheet.value(GlobalStyles.HR_HORSE_SEVENTEEN_TEXT);
            break;
        }
        case 18: {
            background = EStyleSheet.value(GlobalStyles.HR_HORSE_EIGHTEEN_BACKGROUND);
            text = EStyleSheet.value(GlobalStyles.HR_HORSE_EIGHTEEN_TEXT);
            break;
        }
        case 19: {
            background = EStyleSheet.value(GlobalStyles.HR_HORSE_NINETEEN_BACKGROUND);
            text = EStyleSheet.value(GlobalStyles.HR_HORSE_NINETEEN_TEXT);
            break;
        }
        case 20: {
            background = EStyleSheet.value(GlobalStyles.HR_HORSE_TWENTY_BACKGROUND);
            text = EStyleSheet.value(GlobalStyles.HR_HORSE_TWENTY_TEXT);
            break;
        }
        case 21: {
            background = EStyleSheet.value(GlobalStyles.HR_HORSE_TWENTY_ONE_BACKGROUND);
            text = EStyleSheet.value(GlobalStyles.HR_HORSE_TWENTY_ONE_TEXT);
            break;
        }
    }

    return [background, text];
};

const generateStyles = () => {
    return EStyleSheet.create({
        prop: {
            height: 24,
            width: "100%",
            alignItems: "center",
            flexDirection: "row",
            marginVertical: 2.5,
        },
        horseName: {
            color: () => EStyleSheet.value(GlobalStyles.BASE_COLOUR),
            fontWeight: "700",
            fontSize: 12,
            marginRight: "auto",
        },
        odds: {
            color: () => EStyleSheet.value(GlobalStyles.BASE_COLOUR),
            fontWeight: "700",
            textAlign: "right",
            fontSize: 12,
        },
        placeContainer: {
            width: 24,
            height: 24,
            borderRadius: 8,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexWrap: "wrap",
            marginRight: 5,
        },
        placeText: {
            fontSize: 12,
            fontWeight: "700",
        },
    });
};
